/**
 * Finding the model inside a repo that holds more than one.
 *
 * ## The gap this closes
 *
 * `readInventory` looked for `model.safetensors` or `model.safetensors.index.json`
 * **at the repo root**, and nowhere else. Measured over the trending-100, that
 * refused 9 repos — including the single most trending model on the Hub — with
 * "non-standard layout", which was our limitation reported as the model's shape.
 *
 * Their real layouts, from the cached file lists:
 *
 *     KBlueLeaf/TIPOv2-1B-A200M      hf/model.safetensors
 *     LiquidAI/LFM2.5-2.6B-MLX       4bit/ 5bit/ 6bit/ 8bit/ bf16/   (one model, five precisions)
 *     Tongyi-MAI/Z-Image-Turbo       text_encoder/ transformer/ vae/ (a pipeline)
 *     MiniMaxAI/MiniMax-H3           12 components under FL2VA/, Ref2VA/, ...
 *     lightx2v/...-Prompt-Rewriter   adapter_model.safetensors       (a PEFT adapter)
 *
 * Nothing is non-standard about any of them. Every one is a *standard* layout
 * that simply is not at the root, so the existing reader works verbatim once it
 * is pointed at the right prefix.
 *
 * ## Why this returns a list instead of picking
 *
 * A pipeline repo is not one model. `MiniMax-H3`'s text encoder, transformer and
 * audio VAE have different depths, different widths and unrelated layer stacks;
 * concatenating their tensors would produce a grid whose "layer 3" row merges
 * three unrelated things — the exact collision `cellKey` documents and that
 * `towers.ts` exists to prevent *within* a checkpoint.
 *
 * So discovery names the candidates and the caller chooses. One candidate can be
 * opened without asking; several is a question only the visitor can answer,
 * because "the model" in `LFM2.5-2.6B-MLX` means a different file depending on
 * whether you came to look at the 4-bit or the bf16.
 *
 * ## The fallback, and why it is last
 *
 * Some repos follow no convention at all — ComfyUI checkpoints are named for
 * their training run, and a PEFT adapter is `adapter_model.safetensors`. Those
 * are offered as individual files, but *only when no conventional component was
 * found anywhere in the repo*. That ordering matters: `MiniMax-H3` has 104
 * `.safetensors` files, and a picker listing all of them instead of its 12
 * components would be worse than the refusal it replaces.
 *
 * Pure: a file list in, candidates out, no I/O.
 */

/** The conventional payload names, each with an optional shard index beside it. */
const CONVENTIONAL_STEMS = ["model", "diffusion_pytorch_model"] as const;

export interface RepoComponent {
  /**
   * Directory prefix, `""` for the repo root. Includes its trailing slash, so
   * `${prefix}${file}` is always the full path.
   */
  readonly prefix: string;
  /** What to show a visitor: the directory, or the file for a bare candidate. */
  readonly label: string;
  /** The shard index to read, when this component is sharded. */
  readonly indexFile: string | undefined;
  /** The single file to read, when it is not. */
  readonly singleFile: string | undefined;
  /**
   * What this choice costs, when the picker was able to find out.
   *
   * Only GGUF fills these, and only when the sizes request succeeded — a
   * safetensors component is a different model rather than the same one at a
   * different precision, so "which is bigger" is not the question being asked.
   */
  readonly bytes?: number | undefined;
  readonly bitsPerWeight?: number | undefined;
  /** The variant is split; worth saying, because it is read as one model. */
  readonly fileCount?: number | undefined;
}

function dirOf(path: string): string {
  const cut = path.lastIndexOf("/");
  return cut === -1 ? "" : path.slice(0, cut + 1);
}

/**
 * The component to open without asking, or `undefined` when it is a real choice.
 *
 * **A root-level `model.safetensors` is the model.** That is the Hub's own
 * convention, and honouring it is what keeps this change from being a
 * regression: plenty of ordinary repos ship an auxiliary `vae/` or
 * `text_encoder/` beside the checkpoint, and before component discovery existed
 * the root was simply read. Measured — without this rule, four repos in the
 * trending-100 that had always worked started asking a question with an obvious
 * answer.
 *
 * Only when nothing is at the root does the count decide: one is unambiguous,
 * several is the visitor's call.
 */
export function autoComponent(
  components: readonly RepoComponent[],
): RepoComponent | undefined {
  const root = components.find((c) => c.prefix === "");
  if (root) return root;
  return components.length === 1 ? components[0] : undefined;
}

/**
 * Every readable model in the repo, outermost first.
 *
 * Conventional components win outright; bare files are offered only when the
 * repo has none, so a pipeline is never listed as its hundred shards.
 */
export function findComponents(files: readonly string[]): RepoComponent[] {
  const byPrefix = new Map<string, { index?: string; single?: string }>();

  for (const file of files) {
    if (!file.endsWith(".safetensors") && !file.endsWith(".safetensors.index.json")) {
      continue;
    }
    const prefix = dirOf(file);
    const name = file.slice(prefix.length);
    for (const stem of CONVENTIONAL_STEMS) {
      const entry = byPrefix.get(prefix) ?? {};
      if (name === `${stem}.safetensors.index.json`) {
        // An index and a single file can both exist; the index wins, because it
        // is the one that enumerates every shard.
        entry.index = file;
      } else if (name === `${stem}.safetensors`) {
        entry.single ??= file;
      } else {
        continue;
      }
      byPrefix.set(prefix, entry);
    }
  }

  if (byPrefix.size > 0) {
    // Shallowest first, then alphabetically. Sorting on raw length instead puts
    // `vae` above `audio_vae` for no reason a reader scanning the list can see.
    const depth = (p: string) => (p.match(/\//g) ?? []).length;
    return [...byPrefix]
      .sort(([a], [b]) => depth(a) - depth(b) || (a < b ? -1 : 1))
      .map(([prefix, entry]) => ({
        prefix,
        label: prefix === "" ? "model" : prefix.slice(0, -1),
        indexFile: entry.index,
        // Named only when it is what will be read: a sharded component's
        // `model.safetensors` is not a thing to open, and reporting it as one
        // invites a caller to read a single shard as the whole model.
        singleFile: entry.index ? undefined : entry.single,
      }));
  }

  /*
   * No convention anywhere. Offer the files themselves — a ComfyUI checkpoint
   * or a PEFT adapter is a real, readable safetensors file whose only sin is
   * being named for its training run.
   */
  return files
    .filter((f) => f.endsWith(".safetensors"))
    .sort((a, b) => (a < b ? -1 : 1))
    .map((file) => ({
      prefix: dirOf(file),
      label: file,
      indexFile: undefined,
      singleFile: file,
    }));
}

/**
 * What a repo we cannot read appears to hold, named from its own files.
 *
 * **Because guessing was wrong for a real visitor.** The refusal used to end
 * "PyTorch .bin repos aren't supported yet" unconditionally, so
 * `FluidInference/parakeet-tdt-0.6b-v3-coreml` — a CoreML conversion of
 * `nvidia/parakeet-tdt-0.6b-v3`, carrying `.mlmodelc`, `.mlpackage` and `.mil`
 * with CoreML weight blobs in `.bin` — was told it was PyTorch. The `.bin`
 * coincidence is what made the guess plausible and wrong: a CoreML weight file
 * and a pickled PyTorch state dict share an extension and nothing else.
 *
 * Reported by the owner, 2026-08-23, who could open the NVIDIA original and not
 * the conversion.
 *
 * Names a format only on evidence that is specific to it, and otherwise says
 * what it saw rather than inventing a label. A refusal that misidentifies the
 * thing it is refusing is worse than one that admits it does not know: the
 * visitor goes looking for a PyTorch problem they do not have.
 */
export function describeUnreadableRepo(files: readonly string[]): string {
  const has = (re: RegExp) => files.some((f) => re.test(f));

  // Ordered by how specific the evidence is, not by how common the format is.
  if (has(/\.(?:mlmodelc|mlpackage|mil)(?:\/|$)/i)) return "This looks like CoreML";
  if (has(/\.onnx$/i)) return "This looks like ONNX";
  if (has(/\.tflite$/i)) return "This looks like TensorFlow Lite";
  if (has(/\.nemo$/i)) return "This looks like a NeMo checkpoint";
  if (has(/\.msgpack$/i)) return "This looks like Flax";
  if (has(/(?:^|\/)pytorch_model[^/]*\.bin$|\.(?:pt|pth|ckpt)$/i)) {
    return "This looks like PyTorch";
  }
  // A bare `.bin` says nothing on its own — it is the extension that caused the
  // wrong guess in the first place — so it earns no label.
  return "That format isn't one this reads";
}
